/**
 * Safe App: propose a ManifestApproval EIP-712 message to the connected Safe.
 *
 * Flow:
 *   1. SafeAppsSDK handshake with parent (app.safe.global iframe).
 *   2. Pull safe address, chainId, owners, threshold via SDK + RPC.
 *   3. Build EIP-712 typed data (same format as reference/src/eip712.ts).
 *   4. Compute local safeMessageHash for sanity (must equal what wallet shows).
 *   5. On click → sdk.txs.signMessage(typedData) → Safe UI handles signing.
 *   6. Result: messageHash; first owner's wallet popped up; subsequent owners
 *      can confirm via app.safe.global Messages tab.
 */

import SafeAppsSDK from '@safe-global/safe-apps-sdk';
import {
  keccak256,
  encodeAbiParameters,
  parseAbiParameters,
  toHex,
  concat,
  pad,
  createPublicClient,
  http,
  sha256,
  toBytes,
  type Hex,
  type Address,
} from 'viem';

// ---- EIP-712 constants (mirror /reference/src/eip712.ts) ----

const EIP712_DOMAIN_TYPEHASH: Hex =
  '0x47e79534a245952e8b16893a336b85a3d9ea9fa8c573f3d803afb92a79469218';
const SAFE_MSG_TYPEHASH: Hex =
  '0x60b3cbf8b4a223d68d641b3b6ddf9a298e7f33710cf3d3a9d1146b5a6150fbca';
const MANIFEST_APPROVAL_TYPE_STRING =
  'ManifestApproval(bytes32 manifestHash,uint256 version,uint256 notBefore,uint256 notAfter)';
const MANIFEST_APPROVAL_TYPEHASH: Hex = keccak256(toHex(MANIFEST_APPROVAL_TYPE_STRING));

function computeDomainSeparator(chainId: bigint, safe: Hex): Hex {
  return keccak256(
    encodeAbiParameters(parseAbiParameters('bytes32, uint256, address'), [
      EIP712_DOMAIN_TYPEHASH,
      chainId,
      safe,
    ]),
  );
}

function hashApprovalStruct(p: {
  manifestHash: Hex;
  version: bigint;
  notBefore: bigint;
  notAfter: bigint;
}): Hex {
  return keccak256(
    encodeAbiParameters(parseAbiParameters('bytes32, bytes32, uint256, uint256, uint256'), [
      MANIFEST_APPROVAL_TYPEHASH,
      p.manifestHash,
      p.version,
      p.notBefore,
      p.notAfter,
    ]),
  );
}

/**
 * EIP-712 digest of our inner ManifestApproval typed data. This is what
 * MetaMask shows in the "message" field of the SafeMessage primary type.
 */
function hashInnerEip712Digest(
  p: { manifestHash: Hex; version: bigint; notBefore: bigint; notAfter: bigint },
  chainId: bigint,
  safe: Hex,
): Hex {
  const innerStructHash = hashApprovalStruct(p);
  const domainSeparator = computeDomainSeparator(chainId, safe);
  return keccak256(
    concat([
      '0x1901' as Hex,
      pad(domainSeparator, { size: 32 }),
      pad(innerStructHash, { size: 32 }),
    ]),
  );
}

/**
 * Final hash that the wallet actually signs. Safe wraps the inner digest
 * into SafeMessage(bytes message) and applies the EIP-712 envelope again.
 */
function buildSafeMessageHash(
  p: { manifestHash: Hex; version: bigint; notBefore: bigint; notAfter: bigint },
  chainId: bigint,
  safe: Hex,
): Hex {
  const innerDigest = hashInnerEip712Digest(p, chainId, safe);
  const safeMessageStructHash = keccak256(
    encodeAbiParameters(parseAbiParameters('bytes32, bytes32'), [
      SAFE_MSG_TYPEHASH,
      keccak256(innerDigest),
    ]),
  );
  const domainSeparator = computeDomainSeparator(chainId, safe);
  return keccak256(
    concat([
      '0x1901' as Hex,
      pad(domainSeparator, { size: 32 }),
      pad(safeMessageStructHash, { size: 32 }),
    ]),
  );
}

// ---- DOM helpers ----

const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;

function setText(id: string, text: string) {
  const el = $(id);
  if (el) el.textContent = text;
}

function setStatus(msg: string, kind: 'ok' | 'warn' | 'err' = 'warn') {
  const el = $('status');
  el.textContent = msg;
  el.style.color =
    kind === 'ok' ? 'var(--accent)' : kind === 'err' ? 'var(--err)' : 'var(--muted)';
}

function setPill(text: string, kind: 'ok' | 'warn' | 'err') {
  const el = $('conn-pill');
  el.textContent = text;
  el.className = `pill ${kind}`;
}

// ---- main ----

interface SafeContext {
  safeAddress: Hex;
  chainId: bigint;
  owners: Hex[];
  threshold: number;
}

let ctx: SafeContext | null = null;
const sdk = new SafeAppsSDK();

const RPC_URL = 'https://rpc.hyperliquid.xyz/evm';

// Minimal Safe ABI we need
const SAFE_ABI = [
  { inputs: [], name: 'getOwners', outputs: [{ type: 'address[]' }], stateMutability: 'view', type: 'function' },
  { inputs: [], name: 'getThreshold', outputs: [{ type: 'uint256' }], stateMutability: 'view', type: 'function' },
  { inputs: [], name: 'VERSION', outputs: [{ type: 'string' }], stateMutability: 'view', type: 'function' },
] as const;

async function init() {
  try {
    setStatus('Handshaking with Safe UI…');
    const info = await sdk.safe.getInfo();
    // info: { safeAddress, chainId, owners, threshold, isReadOnly, ... }
    const safeAddress = info.safeAddress as Hex;
    const chainId = BigInt(info.chainId);

    // Cross-verify owners/threshold from chain in case SDK lies.
    const client = createPublicClient({ transport: http(RPC_URL) });
    const [owners, threshold] = await Promise.all([
      client.readContract({ address: safeAddress as Address, abi: SAFE_ABI, functionName: 'getOwners' }) as Promise<readonly Address[]>,
      client.readContract({ address: safeAddress as Address, abi: SAFE_ABI, functionName: 'getThreshold' }) as Promise<bigint>,
    ]);

    ctx = {
      safeAddress,
      chainId,
      owners: [...owners].map((a) => a as Hex),
      threshold: Number(threshold),
    };

    setText('ctx-safe', safeAddress);
    setText('ctx-chain', chainId.toString());
    setText('ctx-threshold', `${threshold}`);
    setText('ctx-owners', ctx.owners.join('\n'));

    setPill(`connected (${ctx.owners.length} owners, ${threshold}-of-${ctx.owners.length})`, 'ok');
    $<HTMLButtonElement>('btn-propose').disabled = false;
    setStatus('Ready. Adjust fields above and click "Propose".', 'ok');

    refreshPreview();
  } catch (e) {
    setPill('not in Safe UI', 'err');
    setStatus(
      `SafeApps SDK handshake failed. Open this URL via app.safe.global → Apps → My custom apps → Add custom app. Error: ${(e as Error).message}`,
      'err',
    );
  }
}

function applyURLParams() {
  // Allow the update server to pre-fill the form by appending query params:
  //   ?manifestHash=0x...&version=N&validityHours=168
  // The Safe App SDK runs us inside an iframe — params are on parent's URL,
  // but our iframe URL is also propagated.
  const sp = new URLSearchParams(window.location.search);
  for (const [name, sel] of [
    ['manifestHash', 'in-hash'],
    ['version', 'in-version'],
    ['validityHours', 'in-validity'],
  ] as const) {
    const v = sp.get(name);
    if (v) $<HTMLInputElement>(sel).value = v;
  }
}

function readPayload(): {
  manifestHash: Hex;
  version: bigint;
  notBefore: bigint;
  notAfter: bigint;
} {
  let hash = $<HTMLInputElement>('in-hash').value.trim();
  if (!hash) {
    // deterministic synthetic
    const seed = `expo-safe-codesigning drill ${ctx?.safeAddress ?? 'unknown'} v${
      $<HTMLInputElement>('in-version').value || '1'
    }`;
    hash = sha256(toBytes(seed));
  }
  if (!/^0x[0-9a-fA-F]{64}$/.test(hash)) {
    throw new Error('manifestHash must be 0x + 64 hex chars');
  }
  const version = BigInt($<HTMLInputElement>('in-version').value || '1');
  const validityH = BigInt($<HTMLInputElement>('in-validity').value || '168');
  const now = BigInt(Math.floor(Date.now() / 1000));
  return {
    manifestHash: hash as Hex,
    version,
    notBefore: now - 60n,
    notAfter: now + validityH * 3600n,
  };
}

function refreshPreview() {
  if (!ctx) return;
  try {
    const p = readPayload();
    const ds = computeDomainSeparator(ctx.chainId, ctx.safeAddress);
    const innerDigest = hashInnerEip712Digest(p, ctx.chainId, ctx.safeAddress);
    const msg = buildSafeMessageHash(p, ctx.chainId, ctx.safeAddress);
    setText('prev-ds', ds);
    setText('prev-inner', innerDigest);
    setText('prev-msg', msg);
    setText('typed-data', JSON.stringify(buildTypedData(p, ctx), null, 2));
  } catch (e) {
    setText('prev-msg', `error: ${(e as Error).message}`);
  }
}

function buildTypedData(
  p: { manifestHash: Hex; version: bigint; notBefore: bigint; notAfter: bigint },
  c: SafeContext,
) {
  return {
    types: {
      EIP712Domain: [
        { name: 'chainId', type: 'uint256' },
        { name: 'verifyingContract', type: 'address' },
      ],
      ManifestApproval: [
        { name: 'manifestHash', type: 'bytes32' },
        { name: 'version', type: 'uint256' },
        { name: 'notBefore', type: 'uint256' },
        { name: 'notAfter', type: 'uint256' },
      ],
    },
    primaryType: 'ManifestApproval',
    domain: { chainId: c.chainId.toString(), verifyingContract: c.safeAddress },
    message: {
      manifestHash: p.manifestHash,
      version: p.version.toString(),
      notBefore: p.notBefore.toString(),
      notAfter: p.notAfter.toString(),
    },
  };
}

async function propose() {
  if (!ctx) return;
  try {
    const p = readPayload();
    const typedData = buildTypedData(p, ctx);
    setStatus('Awaiting wallet signature in Safe UI…');
    $<HTMLButtonElement>('btn-propose').disabled = true;

    // sdk.txs.signMessage accepts either string (personal_sign) or EIP-712 object.
    // For typed data we pass it directly. SafeApps SDK 9.x: use signTypedMessage.
    const result = await sdk.txs.signTypedMessage(typedData);
    // Result is OffChainSignMessageResponse | OnChainSignMessageResponse.
    // Off-chain (default for messages): has messageHash. On-chain: has safeTxHash.
    const idLabel =
      'messageHash' in result
        ? `messageHash=${result.messageHash}`
        : `safeTxHash=${result.safeTxHash}`;
    setStatus(
      `Proposed. ${idLabel}. Open the Messages tab in Safe UI to see confirmations from remaining owners (threshold=${ctx.threshold}).`,
      'ok',
    );
  } catch (e) {
    setStatus(`Failed: ${(e as Error).message}`, 'err');
    $<HTMLButtonElement>('btn-propose').disabled = false;
  }
}

// wire up
applyURLParams();
['in-hash', 'in-version', 'in-validity'].forEach((id) =>
  $(id).addEventListener('input', refreshPreview),
);
$<HTMLButtonElement>('btn-propose').addEventListener('click', () => {
  void propose();
});

void init();
